import { writable } from "svelte/store"

export type ToastType = "success" | "error" | "info" | "warning"

export interface Toast {
  id: number
  message: string
  type: ToastType
  duration: number
  remaining: number
  timeoutId?: number
  intervalId?: number
  onClick?: () => void
}

function sanitizeMessage(message: string): string {
  if (typeof message !== "string") {
    return "Invalid message"
  }
  const div = document.createElement("div")
  div.textContent = message
  return div.innerHTML
}

function createToastStore() {
  const { subscribe, update } = writable<Toast[]>([])
  let idCounter = 0

  function send(message: string, type: ToastType = "info", duration = 3000, onClick?: () => void) {
    const id = ++idCounter
    const sanitizedMessage = sanitizeMessage(message)

    const toast: Toast = {
      id,
      message: sanitizedMessage,
      type,
      duration,
      remaining: duration,
      onClick,
    }

    update((toasts) => [...toasts, toast])

    const timeoutId = window.setTimeout(() => {
      dismiss(id)
    }, duration)

    const intervalId = window.setInterval(() => {
      update((toasts) =>
        toasts.map((t) => {
          if (t.id === id) {
            const newRemaining = t.remaining - 100
            return { ...t, remaining: newRemaining >= 0 ? newRemaining : 0 }
          }
          return t
        }),
      )
    }, 100)

    update((toasts) => toasts.map((t) => (t.id === id ? { ...t, timeoutId, intervalId } : t)))
  }

  function dismiss(id: number) {
    update((toasts) => {
      const toast = toasts.find((t) => t.id === id)
      if (toast) {
        if (toast.timeoutId) clearTimeout(toast.timeoutId)
        if (toast.intervalId) clearInterval(toast.intervalId)
      }
      return toasts.filter((t) => t.id !== id)
    })
  }

  function pause(id: number) {
    update((toasts) =>
      toasts.map((toast) => {
        if (toast.id === id) {
          if (toast.timeoutId) {
            clearTimeout(toast.timeoutId)
            toast.timeoutId = undefined
          }
          if (toast.intervalId) {
            clearInterval(toast.intervalId)
            toast.intervalId = undefined
          }
        }
        return toast
      }),
    )
  }

  function resume(id: number) {
    update((toasts) =>
      toasts.map((toast) => {
        if (toast.id === id && !toast.timeoutId) {
          const timeoutId = window.setTimeout(() => {
            dismiss(id)
          }, toast.remaining)

          const intervalId = window.setInterval(() => {
            update((toasts) =>
              toasts.map((t) => {
                if (t.id === id) {
                  const newRemaining = t.remaining - 100
                  return { ...t, remaining: newRemaining >= 0 ? newRemaining : 0 }
                }
                return t
              }),
            )
          }, 100)

          return { ...toast, timeoutId, intervalId }
        }
        return toast
      }),
    )
  }

  return {
    subscribe,
    send,
    dismiss,
    pause,
    resume,
  }
}

export const toasts = createToastStore()

